import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import WhatsAppButton, { WHATSAPP_NUMBER } from '../components/WhatsAppButton';
import SecondOpinionScenarios from '../components/SecondOpinionScenarios';
import FAQ from '../components/FAQ';
import { CheckCircle, Heart, Shield, Clock, Users, WhatsappLogo, Phone, Gift, VideoCamera, CaretDown, CaretUp, MagnifyingGlass, X, FileText, Stethoscope, ShieldCheck } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  const [specialties, setSpecialties] = useState([]);
  const [subspecialties, setSubspecialties] = useState({});
  const [expandedSpec, setExpandedSpec] = useState(null);
  const [showAllSpecialties, setShowAllSpecialties] = useState(false);
  const [specialtySearch, setSpecialtySearch] = useState('');

  useEffect(() => {
    if (refCode) {
      localStorage.setItem('referral_code', refCode);
    }
  }, [refCode]);

  useEffect(() => {
    axios.get(`${API}/specialties`).then(({ data }) => {
      setSpecialties(data.specialties || []);
      setSubspecialties(data.subspecialties || {});
    }).catch(() => {});
  }, []);

  const registerHref = refCode ? `/register?ref=${refCode}` : '/register';

  const features = [
    { icon: Heart, title: 'Expert Second Opinions', description: 'Get professional medical reviews from specialists registered with the NMC or a State Medical Council, across 39 specialties.' },
    { icon: Shield, title: 'Secure & Private', description: "Your records are encrypted and handled under India's Digital Personal Data Protection Act, 2023." },
    { icon: Clock, title: 'Fast Turnaround', description: 'Receive expert opinions within 24-48 hours. No more weeks of waiting.' },
    { icon: Users, title: 'Multi-Specialist Review', description: 'Premium tier includes reviews from multiple specialists for comprehensive analysis.' },
  ];

  const steps = [
    { icon: FileText, number: '01', title: 'Upload Records', description: 'Securely upload your medical reports, scans, and test results.' },
    { icon: Stethoscope, number: '02', title: 'Submit Case', description: 'Describe your symptoms and current diagnosis. Choose your specialty.' },
    { icon: Heart, number: '03', title: 'Expert Review', description: 'A specialist registered with the NMC or a State Medical Council reviews your case.' },
    { icon: CheckCircle, number: '04', title: 'Get Opinion', description: 'Receive detailed written report with treatment recommendations.' },
  ];

  const handleGetStarted = () => {
    if (user) {
      if (user.role === 'patient') navigate('/patient');
      else if (user.role === 'doctor') navigate('/doctor');
      else if (user.role === 'admin') navigate('/admin');
    } else {
      navigate(registerHref);
    }
  };

  return (
    <div className="min-h-screen bg-surface" data-testid="landing-page">
      <Navbar />

      {/* Referral Banner */}
      {refCode && (
        <div
          className="fixed top-16 left-0 right-0 z-40 px-4 py-2 text-center text-sm font-medium"
          style={{ backgroundColor: '#E1EDE5', color: '#3B6953', borderBottom: '1px solid #3B6953' }}
          data-testid="referral-banner"
        >
          <Gift size={16} weight="duotone" className="inline mr-1" />
          Referred by a doctor on our panel · Code: <strong>{refCode}</strong>
        </div>
      )}

      {/* Hero Section — redesigned: left text + right relatable Indian doctor photo */}
      <section
        className="relative pt-28 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden bg-surface"
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: Text + CTA */}
          <div className="text-center md:text-left">
            <p className="text-xs uppercase tracking-[0.2em] text-accent font-bold mb-3" data-testid="hero-eyebrow">
              NMC / State Medical Council registered specialists
            </p>
            <h1 className="font-spectral text-4xl sm:text-5xl lg:text-6xl font-bold text-primary tracking-tight leading-[1.1] mb-5">
              Don't decide your<br />
              health <span className="text-accent">in doubt.</span>
            </h1>
            <p className="text-base sm:text-lg text-secondary mb-7 leading-relaxed max-w-xl mx-auto md:mx-0">
              Get a written second opinion from <strong className="text-primary">senior fellowship-trained specialists</strong> across India — within 24–48 hours. Confirm diagnosis, explore safer treatments, avoid unnecessary surgery.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
              <button
                onClick={handleGetStarted}
                className="btn-primary text-base px-8 py-3 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                data-testid="hero-cta-button"
              >
                Start Your Consultation
              </button>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi, I want to know more about Second Doctor Opinion')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-primary text-primary rounded-full font-medium hover:bg-primary hover:text-white transition-all"
                data-testid="hero-whatsapp-cta"
              >
                <WhatsappLogo size={20} weight="fill" /> Ask on WhatsApp
              </a>
            </div>
            {/* Tiny inline trust line */}
            <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2 justify-center md:justify-start text-xs text-secondary">
              <span className="flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-primary" /> Medico-legal compliant</span>
              <span className="flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-primary" /> Encrypted records</span>
              <span className="flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-primary" /> UPI / GPay accepted</span>
            </div>
          </div>

          {/* Right: Relatable Indian doctor photo in organic rounded shape */}
          <div className="relative">
            <div className="relative aspect-[4/5] max-w-md mx-auto">
              <img
                src="https://images.pexels.com/photos/14558557/pexels-photo-14558557.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Indian doctor consulting patient"
                className="w-full h-full object-cover rounded-t-[120px] rounded-b-3xl shadow-2xl"
                loading="eager"
              />
              {/* Floating mini badge — "24-48h response" */}
              <div className="absolute -bottom-4 -left-2 sm:-left-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 max-w-[220px]">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Clock size={20} weight="duotone" className="text-primary" />
                </div>
                <div>
                  <p className="text-xs text-secondary leading-tight">Typical opinion in</p>
                  <p className="font-bold text-primary text-sm leading-tight">24–48 hours</p>
                </div>
              </div>
              {/* Floating top-right tag */}
              <div className="absolute -top-3 -right-2 sm:-right-6 bg-accent text-white rounded-full px-4 py-2 text-xs font-semibold shadow-lg">
                ₹1,500 onwards
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals Strip */}
      <section className="bg-surface-secondary py-8 px-4 sm:px-6 lg:px-8 border-y border-primary/10" data-testid="trust-signals">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Users, value: '39', label: 'Specialties covered' },
            { icon: FileText, value: 'Cases', label: 'Reviewed across all 39 specialties' },
            { icon: Clock, value: '24-48h', label: 'Typical response time' },
            { icon: Shield, value: 'DPDP', label: 'Act, 2023 data handling' },
          ].map((t, idx) => (
            <div key={idx} className="flex items-center gap-3 justify-center md:justify-start" data-testid={`trust-signal-${idx}`}>
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <t.icon size={24} weight="duotone" className="text-primary" />
              </div>
              <div>
                <p className="font-spectral text-2xl font-bold text-primary leading-none">{t.value}</p>
                <p className="text-xs text-secondary mt-1">{t.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-surface" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-accent font-bold mb-2">How It Works</p>
            <h2 className="font-spectral text-3xl sm:text-4xl font-bold text-primary">Four Simple Steps</h2>
            <p className="text-secondary mt-2 max-w-xl mx-auto">From submission to expert opinion — clear, secure, and fast.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
            {steps.map((step, idx) => (
              <div
                key={idx}
                className="relative bg-surface border border-primary/10 rounded-2xl p-6 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-shadow duration-300"
                data-testid={`step-card-${idx}`}
              >
                {/* Step number watermark */}
                <span className="absolute top-4 right-4 font-spectral text-5xl font-bold text-primary/5 leading-none">
                  {step.number}
                </span>
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                  <step.icon size={28} weight="duotone" className="text-primary" />
                </div>
                <h3 className="font-spectral text-xl font-semibold text-primary mb-2">{step.title}</h3>
                <p className="text-secondary text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-surface-secondary" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-accent font-bold mb-2">Why Choose Us</p>
            <h2 className="font-spectral text-3xl sm:text-4xl font-bold text-primary">Trusted Medical Expertise</h2>
            <p className="text-secondary mt-2 max-w-xl mx-auto">Built for Indian patients — by Indian doctors.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div
                  key={idx}
                  className="bg-surface border border-primary/10 rounded-2xl p-6 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300"
                  data-testid={`feature-card-${idx}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center mb-4">
                    <Icon size={28} weight="duotone" className="text-accent" />
                  </div>
                  <h3 className="font-spectral text-lg font-semibold text-primary mb-2">{feature.title}</h3>
                  <p className="text-secondary text-sm leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Specialties */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-secondary" data-testid="specialties-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-2">Our Specialists</p>
            <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-primary">Medical Specialties &amp; Subspecialties</h2>
            <p className="text-secondary mt-3 max-w-2xl mx-auto">
              We connect you with specialists registered with the NMC or a State Medical Council across {specialties.length} specialties and {Object.values(subspecialties).reduce((a, b) => a + b.length, 0)} subspecialties. Tap a specialty to explore, or <Link to="/specialties" className="underline text-primary" data-testid="landing-all-specialties-link">open every specialty page</Link>.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8 relative">
            <MagnifyingGlass size={20} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-secondary pointer-events-none" />
            <input
              type="text"
              value={specialtySearch}
              onChange={(e) => setSpecialtySearch(e.target.value)}
              placeholder="Search e.g., knee, diabetes, cancer, IVF, heart..."
              className="w-full pl-12 pr-12 py-3 rounded-full border-2 border-primary/20 bg-surface focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 text-primary placeholder:text-secondary/60"
              data-testid="specialty-search-input"
            />
            {specialtySearch && (
              <button
                type="button"
                onClick={() => setSpecialtySearch('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-secondary hover:text-primary"
                data-testid="clear-specialty-search"
                aria-label="Clear search"
              >
                <X size={18} weight="bold" />
              </button>
            )}
          </div>

          {(() => {
            const q = specialtySearch.trim().toLowerCase();
            const isSearching = q.length > 0;

            // Filter: a specialty matches if its name OR any subspecialty matches
            const matched = specialties
              .map((sp) => {
                const subs = subspecialties[sp] || [];
                const specMatch = sp.toLowerCase().includes(q);
                const matchedSubs = subs.filter((s) => s.toLowerCase().includes(q));
                if (!isSearching) return { sp, subs, matchedSubs: [], visible: true };
                if (specMatch || matchedSubs.length > 0) {
                  return { sp, subs, matchedSubs: specMatch ? subs : matchedSubs, visible: true };
                }
                return { sp, subs, matchedSubs: [], visible: false };
              })
              .filter((x) => x.visible);

            const displayed = isSearching
              ? matched
              : (showAllSpecialties ? matched : matched.slice(0, 12));

            if (isSearching && matched.length === 0) {
              return (
                <div className="text-center py-12 text-secondary" data-testid="no-specialty-results">
                  No specialty or subspecialty matches "{specialtySearch}". Try a different keyword or
                  <button
                    type="button"
                    onClick={() => setSpecialtySearch('')}
                    className="ml-1 text-primary underline hover:no-underline"
                  >
                    clear the search
                  </button>.
                </div>
              );
            }

            return (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="specialties-grid">
                  {displayed.map(({ sp, subs, matchedSubs }, idx) => {
                    const isOpen = expandedSpec === sp || (isSearching && matchedSubs.length > 0);
                    return (
                      <div
                        key={sp}
                        className={`bg-surface border rounded-lg p-4 transition-all duration-200 ${
                          isOpen ? 'border-primary shadow-md col-span-1 sm:col-span-2 md:col-span-3 lg:col-span-4' : 'border-primary/20 hover:border-primary/60'
                        }`}
                        data-testid={`specialty-item-${idx}`}
                      >
                        <button
                          type="button"
                          onClick={() => setExpandedSpec(isOpen && !isSearching ? null : sp)}
                          className="w-full flex items-center justify-between text-left"
                          data-testid={`specialty-toggle-${idx}`}
                        >
                          <span className="font-medium text-primary">{sp}</span>
                          {subs.length > 0 && (
                            <span className="flex items-center gap-1 text-xs text-secondary">
                              <span className="hidden sm:inline">{subs.length} subspecialties</span>
                              {isOpen ? <CaretUp size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
                            </span>
                          )}
                        </button>
                        {isOpen && subs.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-primary/10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid={`subspecialty-list-${idx}`}>
                            {(isSearching && matchedSubs.length > 0 ? matchedSubs : subs).map((sub) => (
                              <Link
                                key={sub}
                                to={user ? '/case/submit' : registerHref}
                                className={`text-sm rounded-md px-3 py-2 transition-all ${
                                  isSearching && sub.toLowerCase().includes(q)
                                    ? 'bg-accent/10 text-accent font-medium hover:bg-accent/20'
                                    : 'text-secondary hover:text-primary hover:bg-primary/5'
                                }`}
                                data-testid={`subspecialty-${sub.replace(/\s+/g, '-').toLowerCase()}`}
                              >
                                {sub}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {!isSearching && specialties.length > 12 && (
                  <div className="mt-8 text-center">
                    <button
                      type="button"
                      onClick={() => setShowAllSpecialties(!showAllSpecialties)}
                      className="px-6 py-2 border-2 border-primary text-primary font-medium rounded-full hover:bg-primary hover:text-white transition-all"
                      data-testid="toggle-all-specialties"
                    >
                      {showAllSpecialties ? 'Show Less' : `Show All ${specialties.length} Specialties`}
                    </button>
                  </div>
                )}
                {isSearching && (
                  <p className="mt-6 text-center text-sm text-secondary" data-testid="search-results-count">
                    Showing {matched.length} matching {matched.length === 1 ? 'specialty' : 'specialties'} for "{specialtySearch}"
                  </p>
                )}
              </>
            );
          })()}
        </div>
      </section>

      {/* Doctor Panel Trust Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="doctor-panel-section">
        <div className="max-w-7xl mx-auto">
          <div className="card" style={{ background: 'linear-gradient(135deg, #FDFBF7 0%, #F4F1EB 100%)' }}>
            <div className="grid md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-2">Our Specialist Panel</p>
                <h2 className="font-manrope text-3xl font-bold text-primary mb-3">
                  Verified Senior Indian Specialists
                </h2>
                <p className="text-secondary leading-relaxed mb-4">
                  Every doctor on SecondDoctorOpinion.com is personally screened by our medical advisory team. We verify Medical Council registration, fellowship training, years of practice, and peer reputation.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/doctors" className="btn-primary text-sm" data-testid="landing-browse-doctors">
                    <VideoCamera size={16} weight="duotone" className="inline mr-1" /> Browse Doctors & Book Video Consult
                  </Link>
                  <Link to="/doctors/apply" className="px-6 py-3 text-sm font-medium border-2 border-primary text-primary rounded-full hover:bg-primary hover:text-white transition-all" data-testid="landing-doctor-apply-cta">
                    Apply as a Specialist
                  </Link>
                </div>
              </div>
              <div className="text-center md:text-right">
                <p className="text-4xl font-bold text-primary font-manrope">39</p>
                <p className="text-xs text-secondary uppercase tracking-wider mb-3">Specialties Covered</p>
                <p className="text-2xl font-bold text-accent font-manrope">Registration verified</p>
                <p className="text-xs text-secondary uppercase tracking-wider">NMC / State Medical Council</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SecondOpinionScenarios />

      {/* Founders — Trust & Reliability */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-surface" data-testid="founders-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-accent font-bold mb-2">Meet the Founders</p>
            <h2 className="font-spectral text-3xl sm:text-4xl font-bold text-primary">Doctors building this — not just engineers</h2>
            <p className="text-secondary mt-3 max-w-2xl mx-auto">
              SecondDoctorOpinion.com was founded by a husband-wife surgeon couple. We've been on the other side of the table — explaining complex diagnoses, watching patients struggle for a second view. That's why we built this.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {/* Founder 1 */}
            <div className="bg-surface-secondary border border-primary/10 rounded-3xl overflow-hidden hover:shadow-[0_25px_50px_rgb(0,0,0,0.1)] transition-all duration-300" data-testid="founder-card-0">
              <div className="aspect-[5/4] overflow-hidden bg-surface">
                <img
                  src="https://customer-assets.emergentagent.com/job_medical-consult-pro/artifacts/nc5lc279_IMG_20250214_203808.png"
                  alt="Dr. Shivaam Kesarwaani, Co-Founder"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                  <h3 className="font-spectral text-xl font-bold text-primary">Dr. Shivaam Kesarwaani</h3>
                  <span className="text-xs text-secondary">MBBS, MS (ENT)</span>
                </div>
                <p className="text-sm font-medium text-accent mb-3">Co-Founder &amp; Medical Director</p>
                <p className="text-sm text-secondary leading-relaxed mb-3">
                  Skull Base Surgery Specialist · 10+ Years · International training (Malaysia, Greece). Handles complex ENT, head &amp; neck cancer, and voice / swallowing disorders.
                </p>
                <p className="text-xs text-secondary">WBMC Reg. 72269</p>
              </div>
            </div>

            {/* Founder 2 */}
            <div className="bg-surface-secondary border border-primary/10 rounded-3xl overflow-hidden hover:shadow-[0_25px_50px_rgb(0,0,0,0.1)] transition-all duration-300" data-testid="founder-card-1">
              <div className="aspect-[5/4] overflow-hidden bg-surface">
                <img
                  src="https://customer-assets.emergentagent.com/job_medical-consult-pro/artifacts/a0vn6hem_Untitled%20design%20%283%29.png"
                  alt="Dr. Ankita Mukherjee, Co-Founder"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                  <h3 className="font-spectral text-xl font-bold text-primary">Dr. Ankita Mukherjee</h3>
                  <span className="text-xs text-secondary">MBBS, DLO, DNB (ENT)</span>
                </div>
                <p className="text-sm font-medium text-accent mb-3">Co-Founder &amp; Clinical Director</p>
                <p className="text-sm text-secondary leading-relaxed mb-3">
                  Pediatric ENT Specialist · 10+ Years · Known for her gentle, patient-friendly approach in pediatric ENT, adenoid &amp; chronic ear disorders, and women's ENT care.
                </p>
                <p className="text-xs text-secondary">WBMC Reg. 78745</p>
              </div>
            </div>
          </div>

          {/* Mission strip */}
          <div className="mt-10 bg-primary text-white rounded-3xl p-8 sm:p-10 text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-accent font-bold mb-2">Our Mission</p>
            <p className="font-spectral text-xl sm:text-2xl leading-snug max-w-3xl mx-auto">
              "Every Indian patient deserves a <span className="text-accent">confident, well-informed</span> medical decision — regardless of city, language, or budget."
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 bg-white text-primary rounded-full font-semibold text-sm hover:bg-white/90 transition-colors"
              data-testid="read-our-story-button"
            >
              Read our full story →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ />

      {/* For Doctors — Practice Management Promo */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-primary text-white relative overflow-hidden" data-testid="for-doctors-section">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #C66B3D 0%, transparent 70%)' }} />
        <div className="max-w-6xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Left: Pitch */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-accent font-bold mb-3">For Doctors</p>
              <h2 className="font-spectral text-3xl sm:text-4xl lg:text-5xl font-bold mb-5 leading-tight">
                Run your <span className="text-accent">entire practice</span> online — for free
              </h2>
              <p className="text-white/85 text-base sm:text-lg leading-relaxed mb-6 max-w-xl">
                Indian specialists use SecondDoctorOpinion.com to manage clinics, accept appointments and track revenue. <strong className="text-accent">No subscription. No setup fee.</strong> Your NMC / State Medical Council registration is verified before your profile goes live.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {[
                  { icon: '🏥', title: 'Multi-clinic dashboard', desc: 'Manage 1 or 10 clinics from one place' },
                  { icon: '📅', title: 'Online appointments', desc: 'Patients book your free slots 24/7' },
                  { icon: '💰', title: 'Revenue analytics', desc: 'Daily / monthly earnings + charts' },
                  { icon: '👥', title: 'Patient records', desc: 'OPD log + searchable history' },
                  { icon: '🌐', title: 'Public profile page', desc: 'Shareable doctor URL with SEO' },
                  { icon: '🤝', title: 'Second opinion cases', desc: 'Earn from expert consultations' },
                ].map((f, idx) => (
                  <div key={idx} className="bg-white/10 backdrop-blur rounded-xl p-3 flex items-start gap-3" data-testid={`doctor-perk-${idx}`}>
                    <span className="text-2xl flex-shrink-0">{f.icon}</span>
                    <div>
                      <p className="font-semibold text-sm">{f.title}</p>
                      <p className="text-xs text-white/75 leading-snug">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/doctors/apply"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-accent text-white rounded-full font-semibold hover:bg-accent/90 transition-all hover:-translate-y-0.5"
                  data-testid="apply-as-doctor-cta"
                >
                  Apply as a Doctor — Free →
                </Link>
                <Link
                  to="/register?role=doctor"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-white/40 text-white rounded-full font-medium hover:bg-white/10 transition-colors"
                  data-testid="quick-doctor-signup-cta"
                >
                  Quick sign up
                </Link>
              </div>

              {/* Trust line */}
              <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2 text-xs text-white/70">
                <span>✓ No commission for first 6 months</span>
                <span>✓ NMC / State Medical Council verified</span>
                <span>✓ Cancel anytime</span>
              </div>
            </div>

            {/* Right: Mock dashboard preview */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-5 text-primary transform rotate-1">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-spectral text-lg font-bold">My Practice</p>
                    <p className="text-xs text-secondary">Dr. R. Sharma · Cardiology</p>
                  </div>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">Live</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="bg-surface-secondary rounded-lg p-3 text-center">
                    <p className="text-xs text-secondary uppercase">Today</p>
                    <p className="font-spectral text-xl font-bold">₹14k</p>
                  </div>
                  <div className="bg-surface-secondary rounded-lg p-3 text-center">
                    <p className="text-xs text-secondary uppercase">Month</p>
                    <p className="font-spectral text-xl font-bold">₹2.8L</p>
                  </div>
                  <div className="bg-surface-secondary rounded-lg p-3 text-center">
                    <p className="text-xs text-secondary uppercase">Appts</p>
                    <p className="font-spectral text-xl font-bold">142</p>
                  </div>
                </div>
                <div className="flex items-end gap-1 h-20 mb-3">
                  {[40, 65, 55, 80, 70, 90, 75, 95, 60, 85, 100, 78, 88, 92].map((h, i) => (
                    <div key={i} className="flex-1 bg-primary rounded-t" style={{ height: `${h}%` }} />
                  ))}
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between py-1.5 px-2 bg-surface-secondary rounded">
                    <span>10:00 · Priya M.</span><span className="text-green-600 font-medium">Confirmed</span>
                  </div>
                  <div className="flex justify-between py-1.5 px-2 bg-surface-secondary rounded">
                    <span>10:30 · Vikram R.</span><span className="text-green-600 font-medium">Confirmed</span>
                  </div>
                  <div className="flex justify-between py-1.5 px-2 bg-surface-secondary rounded">
                    <span>11:00 · Open</span><span className="text-secondary">Available</span>
                  </div>
                </div>
              </div>
              <p className="mt-3 text-xs text-white/70 text-center" data-testid="sample-dashboard-note">Sample dashboard with illustrative data</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}

      {/* Quick Consult / WhatsApp Section */}

      {/* Pricing */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-2">Pricing — India</p>
            <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-primary mb-4">Choose Your Plan</h2>
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
              style={{ backgroundColor: '#E1EDE5', color: '#3B6953', border: '1px solid #3B6953' }}
              data-testid="marketing-trust-badge"
            >
              <Heart size={16} weight="duotone" />
              <span>Every opinion: AI-assisted draft, reviewed and signed by a specialist registered with the NMC or a State Medical Council</span>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Basic */}
            <div className="card bg-surface-secondary" data-testid="basic-tier-card">
              <h3 className="font-manrope text-2xl font-bold text-primary mb-2">Basic Consultation</h3>
              <div className="text-4xl font-bold text-primary mb-1">₹1,500</div>
              <p className="text-xs text-secondary mb-6">One-time fee</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} weight="fill" className="text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-secondary">Written summary opinion</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} weight="fill" className="text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-secondary">Diagnosis review by specialist</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} weight="fill" className="text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-secondary">General treatment guidance</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} weight="fill" className="text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-secondary">Hospital recommendations</span>
                </li>
              </ul>
              <button
                onClick={() => navigate('/register')}
                className="w-full py-3 border-2 border-primary text-primary font-semibold rounded-full hover:bg-primary hover:text-white transition-all"
                data-testid="basic-tier-cta"
              >
                Get Basic Consultation
              </button>
            </div>

            {/* Premium */}
            <div
              className="card relative"
              style={{
                backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/1e1aa61b-717a-46d3-8753-c0c32ffb2bf6/images/34dd1f4ae55ce74851a6401587765b8066fb00fbe3b3e5e98d5a3c67634cef07.png')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
              data-testid="premium-tier-card"
            >
              <div className="absolute inset-0 bg-primary/90 rounded-lg"></div>
              <div className="relative z-10 text-white">
                <h3 className="font-manrope text-2xl font-bold mb-2">Premium Detailed Consultation</h3>
                <div className="text-4xl font-bold mb-1">₹2,500</div>
                <p className="text-xs text-white/70 mb-6">One-time fee</p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={20} weight="fill" className="text-accent mt-0.5 flex-shrink-0" />
                    <span>Multi-specialist review</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={20} weight="fill" className="text-accent mt-0.5 flex-shrink-0" />
                    <span>Detailed video consultation (30-60 min)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={20} weight="fill" className="text-accent mt-0.5 flex-shrink-0" />
                    <span>Personalized treatment plan</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={20} weight="fill" className="text-accent mt-0.5 flex-shrink-0" />
                    <span>Follow-up support sessions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={20} weight="fill" className="text-accent mt-0.5 flex-shrink-0" />
                    <span>Cost comparison across regions</span>
                  </li>
                </ul>
                <button
                  onClick={() => navigate('/register')}
                  className="w-full py-3 bg-accent text-white font-semibold rounded-full hover:bg-accent/90 transition-all"
                  data-testid="premium-tier-cta"
                >
                  Get Premium Consultation
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Consult / WhatsApp Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-secondary" data-testid="whatsapp-section">
        <div className="max-w-4xl mx-auto">
          <div className="card text-center" style={{ borderColor: '#25D366' }}>
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
              style={{ backgroundColor: '#25D366' }}
            >
              <WhatsappLogo size={36} weight="fill" className="text-white" />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: '#25D366' }}>
              Quick Consult
            </p>
            <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-primary mb-3">
              Talk to us on WhatsApp
            </h2>
            <p className="text-secondary mb-6 max-w-2xl mx-auto leading-relaxed">
              Have a question before booking? Chat with our care team on WhatsApp — share your reports, get tier guidance, or pay directly via Google Pay (UPI).
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi, I would like to book a second medical opinion consultation.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-8 py-3 rounded-full font-semibold text-white transition-transform hover:scale-105"
                style={{ backgroundColor: '#25D366' }}
                data-testid="whatsapp-cta-button"
              >
                <WhatsappLogo size={22} weight="fill" />
                Chat on WhatsApp
              </a>
              <a
                href="tel:+918840804798"
                className="flex items-center gap-2 px-8 py-3 rounded-full font-semibold text-primary border-2 border-primary hover:bg-primary hover:text-white transition-all"
                data-testid="phone-cta-button"
              >
                <Phone size={22} weight="duotone" />
                +91 88408 04798
              </a>
            </div>
            <p className="text-xs text-secondary">
              Available Mon–Sat, 9 AM – 9 PM IST · Pay via <strong>Google Pay / UPI</strong> to <strong>shivaam.kesarwaani-1@oksbi</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary text-white py-12 px-4 sm:px-6 lg:px-8" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 font-spectral text-xl font-semibold mb-3">
                <Heart size={24} weight="duotone" />
                SecondDoctorOpinion.com
              </div>
              <p className="text-sm text-white/80 leading-relaxed">
                Independent second medical opinions from verified Indian specialists. Make informed decisions about your health.
              </p>
            </div>

            <div>
              <h4 className="font-manrope font-semibold mb-3 text-sm uppercase tracking-wider">Service</h4>
              <ul className="space-y-2 text-sm text-white/80">
                <li><Link to="/about" className="hover:text-white" data-testid="footer-about">About Us</Link></li>
                <li><Link to="/doctors" className="hover:text-white" data-testid="footer-find-doctors">Find Doctors</Link></li>
                <li><Link to="/specialties" className="hover:text-white" data-testid="footer-specialties">All Specialties</Link></li>
                <li><Link to="/blog" className="hover:text-white" data-testid="footer-blog">Patient Guides &amp; Blog</Link></li>
                <li><Link to="/contact" className="hover:text-white" data-testid="footer-contact">Contact Us</Link></li>
                <li><Link to="/doctors/apply" className="hover:text-white" data-testid="footer-for-doctors">For Doctors</Link></li>
                <li><Link to="/register" className="hover:text-white">Get Started</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-manrope font-semibold mb-3 text-sm uppercase tracking-wider">Legal</h4>
              <ul className="space-y-2 text-sm text-white/80">
                <li><Link to="/privacy-policy" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</Link></li>
                <li><Link to="/terms-of-use" className="hover:text-white" data-testid="footer-terms">Terms of Use</Link></li>
                <li><Link to="/medical-disclaimer" className="hover:text-white" data-testid="footer-disclaimer">Medical Disclaimer</Link></li>
                <li><Link to="/refund-policy" className="hover:text-white" data-testid="footer-refund">Refund Policy</Link></li>
                <li><Link to="/grievance-redressal" className="hover:text-white" data-testid="footer-grievance">Grievance Redressal</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-manrope font-semibold mb-3 text-sm uppercase tracking-wider">Reach Us</h4>
              <ul className="space-y-2 text-sm text-white/80">
                <li>WhatsApp: +91 88408 04798</li>
                <li>support@seconddoctoropinion.com</li>
                <li>Mon–Sat, 9 AM – 9 PM IST</li>
                <li data-testid="footer-address">Lucknow, Uttar Pradesh, India</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/20 pt-6">
            <div className="bg-white/5 rounded-lg p-4 mb-4 text-xs text-white/70 leading-relaxed">
              <strong className="text-white/90">Important Medical Disclaimer:</strong> SecondDoctorOpinion.com is an informative platform that connects patients with independent licensed specialists for second medical opinions. The content and opinions provided are not a substitute for in-person physician consultation, emergency care, or your treating physician's advice. Specialists do not physically examine you, and their opinions are based solely on records you submit. For medical emergencies, call 112 immediately. See our full <Link to="/medical-disclaimer" className="underline">Medical Disclaimer</Link>.
            </div>
            <p className="text-white/60 text-sm text-center">© 2026 SecondDoctorOpinion.com. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <WhatsAppButton />
    </div>
  );
}